/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import * as React from 'react';
import { BsFillCheckCircleFill } from 'react-icons/bs';
import { useEffect, useRef, useState } from 'react';
import { HiMicrophone } from 'react-icons/hi';
import { confirmWrapper } from 'src/components/Dialog';
import { CircularProgress } from '@mui/material';
import { twMerge } from 'tailwind-merge';
import Button from './Button';
import ApplyNowHeader from './ApplyNowHeader';
import API from '../../../network';
import PlayMic from './WobbleEffect';

interface AssesmentNewProps {
  sessionId: string;
  endAssesment: () => Promise<void>;
  resumeName: string;
  jobTitle: string;
  setGlobalLoading: (loading: boolean) => void;
  candidateName: string | null;
}
export default function AssesmentNew({
  sessionId, endAssesment, resumeName, jobTitle, setGlobalLoading, candidateName,
}: AssesmentNewProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [question, setQuestion] = useState<string>('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(1);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  const fetchFirstQuestion = async () => {
    setLoading(true);
    try {
      const response = await API.getInstance().generateQuestion({ session_id: sessionId });
      setQuestion(response.question);
    } catch (error) {
      return;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFirstQuestion();
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      const chunks: BlobPart[] = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      await confirmWrapper(
        'Error starting recording',
        {
          variant: 'danger',
          hidePrimaryBtn: true,
          hideSecondaryBtn: true,
          fullWidth: true,
          showCloseButton: true,
        },
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleAnswerSubmit = async () => {
    if (!audioBlob) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'answer.webm');

      formData.append('s3FolderName', 'assessment');

      const saveAudioResponse = await API.getInstance().saveFileToS3(formData);
      const audioFilePath = saveAudioResponse;

      await API.getInstance().transcribeAudio({
        session_id: sessionId,
        audio_file_path: audioFilePath,
      });

      setAudioBlob(null);
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);

      const response = await API.getInstance().generateQuestion({ session_id: sessionId });
      setQuestion(response.question);
    } catch (error) {
      await confirmWrapper(
        'Error submitting answer',
        {
          variant: 'danger',
          hidePrimaryBtn: true,
          hideSecondaryBtn: true,
          fullWidth: true,
          showCloseButton: true,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEndAssessment = async () => {
    if (!audioBlob) return;

    setGlobalLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'answer.webm');

      formData.append('s3FolderName', 'assessment');

      const saveAudioResponse = await API.getInstance().saveFileToS3(formData);
      const audioFilePath = saveAudioResponse;

      await API.getInstance().transcribeAudio({
        session_id: sessionId,
        audio_file_path: audioFilePath,
      });

      setAudioBlob(null);
    } catch (error) {
      await confirmWrapper(
        'Error submitting answer',
        {
          variant: 'danger',
          hidePrimaryBtn: true,
          hideSecondaryBtn: true,
          maxWidth: 'sm',
          fullWidth: true,
          showCloseButton: true,
        },
      );
    } finally {
      setGlobalLoading(false);
      endAssesment();
    }
  };

  const speakQuestion = () => {
    const utterance = new SpeechSynthesisUtterance(question);
    utterance.lang = 'en-US';
    speechSynthesis.speak(utterance);
  };

  return (
    <div className="mx-auto w-full max-w-[660px]">
      <ApplyNowHeader resumeName={resumeName} candidateName={candidateName} />

      <section className="space-y-6">
        <h2 className="mb-2 font-semibold text-lg">
          Job Profile -
          {' '}
          {jobTitle}
        </h2>
        <div className="gap-4 border-[#d9d9db] grid grid-cols-7 mt-0 p-4 border border-solid rounded-lg">
          {loading ? (
            <div className="flex flex-col justify-center items-center gap-8 col-span-5 h-full">
              <CircularProgress className="w-10 h-10 text-[#00349A]" />
            </div>
          ) : (
            <div className="flex flex-col justify-between col-span-5 w-full h-full">
              <div className="flex items-center font-bold text-[#00349A] text-xl">
                <button type="button" onClick={speakQuestion} className="bg-transparent border-none cursor-pointer">
                  <img src="/speaker.svg" alt="Upload Resume" width="27" height="27" />
                </button>
                <span className="ml-2">
                  {question.replace(/^Question:\s*/i, '').replace(/^Question:\s*/i, '')}
                </span>
              </div>
              <div className="relative flex flex-col justify-center items-center bg-[#F0F3F980]/50 py-4 rounded w-full h-[173px]">
                {audioBlob && !isRecording && (
                  <audio controls src={URL.createObjectURL(audioBlob)} className="mx-auto w-[80%]">
                    <track kind="captions" src="" label="English captions" />
                  </audio>
                )}

                <Button
                  variant="contained"
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`w-20 h-20 relative rounded-full ${isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'}`}
                >
                  {!isRecording ? <HiMicrophone className="text-[#00349A] text-4xl" /> : <PlayMic />}
                </Button>
                {/* <WobbleEffect /> */}
                <span className="font-medium text-gray-600 text-sm">
                  {isRecording ? 'Stop recording' : audioBlob ? 'Re-Record' : 'Start recording'}
                </span>
              </div>
            </div>
          )}
          {/* {Questions right slab} */}
          <div className="space-y-1 col-span-2">
            {new Array(5).fill(0).map((_, index) => (
              <div
                className={twMerge(
                  'flex justify-between items-center px-6 py-2 rounded-md min-h-[44px] text-sm',
                  index < currentQuestionIndex - 1
                    ? 'bg-[#E9F0FD] text-[#00349A]'
                    : 'bg-[#F8F9FC] text-gray-400',
                  index + 1 === currentQuestionIndex && 'border-[#00349A]/20 border border-solid',
                )}
                key={`${index + 23}sfs`}
              >
                Question
                {' '}
                {index + 1}
                <BsFillCheckCircleFill
                  className={`w-4 h-4 ${index < currentQuestionIndex - 1 ? 'text-[#00349A]' : 'text-secondary hidden'}`}
                />
              </div>
            ))}
          </div>

        </div>
        <div className="flex justify-center items-center space-x-4">
          {currentQuestionIndex !== 5 ? (
            <Button
              variant="contained"
              className="disabled:bg-[#2065EC]/10 disabled:text-white"
              disabled={!audioBlob || isRecording || loading}
              onClick={handleAnswerSubmit}
            >
              Next
            </Button>
          )
            : (
              <Button
                variant="contained"
                className="disabled:bg-[#2065EC]/10 disabled:text-white"
                disabled={!audioBlob || isRecording || loading}
                onClick={handleEndAssessment}
              >
                Submit
              </Button>
            )}
        </div>
      </section>

    </div>
  );
}
